import type { BackupVideoCodec, VideoCodec } from 'livekit-client';

/**
 * Preferência de codec de vídeo: VP8 primeiro (default LiveKit).
 *
 * Por que não H.264 como primário (set/2026 — relatos de "vermelho ao ligar a câmera"):
 * - Com simulcast (3 camadas) o Chrome codifica H.264 em software (OpenH264) — 3 encoders
 *   por câmera + 1–2 pela tela. Em notebooks comuns isso dispara `qualityLimitationReason=cpu`
 *   e o browser desliga as camadas altas → o SFU (`DistanceToDesired`) rebaixa o score do
 *   publisher para Poor mesmo com 0 % de perda.
 * - O rate control do OpenH264 estoura o `maxBitrate` em movimento/keyframe, gerando perda
 *   real no uplink que congela áudio/tela quando câmera + tela publicam juntos.
 * - Os `VideoPresets` do livekit-client são calibrados para VP8; H.264 usa libvpx-equivalente
 *   só com hardware confiável, o que não controlamos no parque de clientes.
 * - VP8 mantém `backupCodec` desligado (sem publish duplo) e é universal (Chrome/Firefox/
 *   Safari ≥ 12.1, egress, E2EE).
 *
 * `?codec=h264` continua disponível para testes A/B (`requestedCodec`).
 */
export const VIDEO_CODEC_PREFERENCE_ORDER = [
  'vp8',
  'h264',
  'vp9',
  'h265',
  'av1',
] as const satisfies readonly VideoCodec[];

export const DEFAULT_VIDEO_CODEC: VideoCodec = VIDEO_CODEC_PREFERENCE_ORDER[0];

const E2EE_UNSUPPORTED_CODECS: ReadonlySet<VideoCodec> = new Set(['h265', 'vp9', 'av1']);

const MIME_TO_CODEC: Array<{ pattern: RegExp; codec: VideoCodec }> = [
  { pattern: /h264|avc1/, codec: 'h264' },
  { pattern: /h265|hev1|hvc1/, codec: 'h265' },
  { pattern: /vp9/, codec: 'vp9' },
  { pattern: /vp8/, codec: 'vp8' },
  { pattern: /av1/, codec: 'av1' },
];

export type ResolveVideoPublishCodecOptions = {
  /** Codec explícito (`?codec=`); omitido = escolher pela ordem de preferência. */
  requestedCodec?: VideoCodec;
  e2eeEnabled?: boolean;
  /** Override para testes; usa RTCRtpSender.getCapabilities no browser. */
  supportedCodecs?: ReadonlySet<VideoCodec>;
};

function mimeTypeToCodec(mimeType: string): VideoCodec | undefined {
  const mime = mimeType.toLowerCase();
  for (const { pattern, codec } of MIME_TO_CODEC) {
    if (pattern.test(mime)) return codec;
  }
  return undefined;
}

/** Codecs que o browser consegue publicar (encode), na ordem exposta pelo WebRTC. */
export function getBrowserSupportedPublishCodecs(): Set<VideoCodec> {
  const supported = new Set<VideoCodec>();

  if (typeof RTCRtpSender !== 'undefined' && typeof RTCRtpSender.getCapabilities === 'function') {
    const capabilities = RTCRtpSender.getCapabilities('video');
    for (const entry of capabilities?.codecs ?? []) {
      const codec = mimeTypeToCodec(String(entry.mimeType ?? ''));
      if (codec) supported.add(codec);
    }
  }

  if (supported.size === 0) {
    supported.add('h264');
    supported.add('vp8');
  }

  return supported;
}

function isAllowedForSession(codec: VideoCodec, e2eeEnabled: boolean): boolean {
  if (!e2eeEnabled) return true;
  return !E2EE_UNSUPPORTED_CODECS.has(codec);
}

function pickFromPreferenceOrder(
  order: readonly VideoCodec[],
  supported: ReadonlySet<VideoCodec>,
  e2eeEnabled: boolean,
): VideoCodec {
  for (const codec of order) {
    if (!isAllowedForSession(codec, e2eeEnabled)) continue;
    if (supported.has(codec)) return codec;
  }
  return DEFAULT_VIDEO_CODEC;
}

/**
 * Resolve o codec primário de publicação.
 * Sem `requestedCodec`, percorre VIDEO_CODEC_PREFERENCE_ORDER até achar suporte no browser.
 */
export function resolveVideoPublishCodec(options: ResolveVideoPublishCodecOptions = {}): VideoCodec {
  const { requestedCodec, e2eeEnabled = false } = options;
  const supported = options.supportedCodecs ?? getBrowserSupportedPublishCodecs();

  if (
    requestedCodec &&
    isAllowedForSession(requestedCodec, e2eeEnabled) &&
    supported.has(requestedCodec)
  ) {
    return requestedCodec;
  }

  return pickFromPreferenceOrder(VIDEO_CODEC_PREFERENCE_ORDER, supported, e2eeEnabled);
}

/** Backup LiveKit (só vp8/h264): H.264 quando o primário é avançado; VP8 quando primário é H.264. */
export function resolveBackupCodec(
  primaryCodec: VideoCodec,
): { codec: BackupVideoCodec } | false {
  if (primaryCodec === 'vp8') {
    return false;
  }
  if (primaryCodec === 'h264') {
    return { codec: 'vp8' };
  }
  return { codec: 'h264' };
}

export function formatVideoCodecPreferenceList(): string {
  return VIDEO_CODEC_PREFERENCE_ORDER.join(', ');
}
